import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import { activeMs, buildBody, captureContext, isPersisted, newRecorder, precheck, recoverInterrupted, reduce, type CaptureContext, type Ev, type GpsEnv, type Recorder } from "./gps";
import { isApp, nativePlugin } from "./platform";
import { t } from "../i18n";
import { openGpsStore } from "./gpsStore";
import {
  canStart, confirmSession, FLUSH_NOW, interrupted, LIMITS, newSession, Persister, purge, syncAll, type GpsStore, type Post, type Session,
} from "./gpsSync";

/**
 * v0.16.2/0.16.3 — liga a máquina de estados (`lib/gps.ts`) ao GPS do aparelho e ao armazenamento local.
 * - O `watchPosition` existe só em requesting_location/recording e é encerrado em qualquer outro estado e ao sair.
 * - Pede permissão apenas no clique em Iniciar (nunca ao abrir a página).
 * - v0.18.0: no APP, o GPS vem do plugin nativo BackgroundGeolocation (serviço em primeiro plano do Android, com
 *   notificação fixa "Gravando corrida"): continua com a TELA BLOQUEADA e com o app em segundo plano, sem manter a tela
 *   acesa. No NAVEGADOR não há GPS em segundo plano (limite do navegador): lá a tela acesa continua sendo pedida.
 * - B4: cada evento é guardado no aparelho (IndexedDB) em lotes de ~3 s e na hora em pausa/fim; ao confirmar o corpo
 *   também fica no aparelho antes do envio. O envio só roda com a página aberta (sem sincronização em segundo plano).
 * - Coordenadas não vão para console nem para URLs.
 */

interface WakeSentinel { release: () => Promise<void> }

/** Localização entregue pelo plugin nativo (@capacitor-community/background-geolocation). */
interface NativeLocation { latitude: number; longitude: number; accuracy: number; altitude: number | null; time: number | null }
interface NativeGeo {
  addWatcher: (o: Record<string, unknown>, cb: (l?: NativeLocation, e?: { code?: string }) => void) => Promise<string> | string;
  removeWatcher: (o: { id: string }) => Promise<void>;
}
/** GPS em segundo plano: só no app que tem o plugin nativo. */
const backgroundGeo = (): NativeGeo | null => nativePlugin("BackgroundGeolocation");

async function readEnv(): Promise<GpsEnv> {
  if (backgroundGeo()) return { secure: true, hasGeo: true, permission: "unknown" };   // o plugin pede a permissão nativa
  const secure = typeof window !== "undefined" && window.isSecureContext;
  const hasGeo = typeof navigator !== "undefined" && "geolocation" in navigator;
  let permission: GpsEnv["permission"] = "unknown";
  try {
    const s = await navigator.permissions?.query({ name: "geolocation" as PermissionName });
    if (s) permission = s.state as GpsEnv["permission"];
  } catch { /* Safari antigo: sem Permissions API */ }
  return { secure, hasGeo, permission };
}

const newId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto
  ? crypto.randomUUID()
  : "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)));

const post: Post = async (orgId, body) => {
  try {
    const d = await api.post<{ activity_id: string; points_kept: number }>(`/orgs/${orgId}/me/gps-recordings`, body);
    return { status: 200, data: d };
  } catch (e) {
    if (e instanceof ApiError) return { status: e.status, code: e.code, message: e.message };
    return { status: 0 };
  }
};

export function useGpsRecorder(orgId: string, userId: string) {
  const [r, dispatch] = useReducer(reduce, undefined, newRecorder);
  const [now, setNow] = useState(() => Date.now());
  const [wakeLock, setWakeLock] = useState<"on" | "off" | "unsupported">("off");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [storage, setStorage] = useState<"loading" | "ready" | "unavailable">("loading");
  const [syncing, setSyncing] = useState(false);
  const [persistFull, setPersistFull] = useState(false);
  const [persistError, setPersistError] = useState(false);   // o aparelho não está conseguindo guardar (cheio ou bloqueado)
  const [saveError, setSaveError] = useState(false);          // Salvar falhou: a gravação segue na memória, tente de novo
  const [sentDirect, setSentDirect] = useState<string | null>(null);   // atividade enviada sem cópia local (armazenamento falhou)
  const watch = useRef<number | null>(null);
  const nativeWatch = useRef<Promise<string> | null>(null);   // id do watcher nativo (assíncrono)
  const lastFlush = useRef(0);
  const background = !!backgroundGeo();
  const wake = useRef<WakeSentinel | null>(null);
  const store = useRef<GpsStore | null>(null);
  const persister = useRef<Persister | null>(null);
  const rRef = useRef<Recorder>(r);
  const sessionsRef = useRef<Session[]>([]);
  const status = r.status;

  const refresh = useCallback(async () => {
    if (!store.current) return;
    const list = await store.current.listSessions(userId);
    sessionsRef.current = list;
    setSessions(list);
  }, [userId]);

  const snapshot = () => ({ distanceM: rRef.current.tracker.distanceM, activeS: Math.round(activeMs(rRef.current, Date.now()) / 1000) });
  const flush = useCallback(async () => {
    const p = persister.current;
    if (p && p.pending > 0) {
      try { await p.flush(snapshot(), Date.now()); setPersistError(false); }
      catch { setPersistError(true); /* os eventos ficam no buffer e vão no próximo lote */ }
    }
  }, []);

  /** Aplica o evento (estado local imediato) e guarda no aparelho. */
  const send = useCallback((ev: Ev) => {
    const cur = rRef.current;
    rRef.current = reduce(cur, ev);
    dispatch(ev);
    if (!store.current || !isPersisted(ev)) return;
    if (ev.type === "fix" && !persister.current) {
      if (cur.status !== "requesting_location") return;
      persister.current = new Persister(store.current, newSession(ev.id, userId, orgId, ev.now));
    }
    const p = persister.current;
    if (!p) return;
    if (!p.push(ev, "now" in ev ? ev.now : 0)) setPersistFull(true);
    // com a tela bloqueada os temporizadores do WebView podem atrasar: o próprio GPS dispara o lote quando passou do prazo
    const due = "now" in ev && ev.now - lastFlush.current >= LIMITS.flushMs;
    if (FLUSH_NOW.includes(ev.type) || due) { if ("now" in ev) lastFlush.current = ev.now; void flush(); }
  }, [flush, orgId, userId]);

  // ---------------------------------------------------------------- envio ao servidor
  const syncNow = useCallback(async (force = false) => {
    const s = store.current;
    if (!s) return;
    const run = async () => {
      setSyncing(true);
      try { await syncAll(s, userId, post, Date.now(), force); } catch { /* armazenamento falhou: a cópia continua onde estava */ }
      setSyncing(false);
      await refresh();
    };
    const locks = (navigator as unknown as { locks?: { request: (n: string, o: { ifAvailable: boolean }, cb: (l: unknown) => Promise<void>) => Promise<void> } }).locks;
    if (locks) await locks.request("eapace-gps-sync", { ifAvailable: true }, async (lock) => { if (lock) await run(); });   // uma aba por vez
    else await run();
  }, [refresh, userId]);

  // abertura: banco, limpeza, lista e primeira tentativa de envio
  useEffect(() => {
    let alive = true;
    void (async () => {
      const s = await openGpsStore();
      if (!alive) return;
      if (!s) { setStorage("unavailable"); return; }
      store.current = s;
      setStorage("ready");
      try { await purge(s, userId, Date.now()); } catch { /* limpeza é opcional */ }
      await refresh();
      void syncNow(false);
    })();
    return () => { alive = false; };
  }, [refresh, syncNow, userId]);

  // novas tentativas: voltou a conexão, e a cada 30 s enquanto a página está aberta
  useEffect(() => {
    const online = () => void syncNow(true);
    window.addEventListener("online", online);
    const id = window.setInterval(() => { if (sessionsRef.current.some((x) => x.phase === "pending_sync")) void syncNow(false); }, 30_000);
    return () => { window.removeEventListener("online", online); window.clearInterval(id); };
  }, [syncNow]);

  // lotes: a cada 3 s, e ao ocultar/fechar a página
  useEffect(() => {
    const id = window.setInterval(() => void flush(), LIMITS.flushMs);
    const hide = () => { if (document.visibilityState === "hidden") void flush(); };
    const gone = () => void flush();
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("pagehide", gone);
    return () => { window.clearInterval(id); document.removeEventListener("visibilitychange", hide); window.removeEventListener("pagehide", gone); };
  }, [flush]);

  // ---------------------------------------------------------------- GPS e tela
  const stopWatch = useCallback(() => {
    if (watch.current !== null) { navigator.geolocation.clearWatch(watch.current); watch.current = null; }
    const nw = nativeWatch.current;
    nativeWatch.current = null;
    if (nw) void nw.then((id) => backgroundGeo()?.removeWatcher({ id })).catch(() => undefined);   // encerra o serviço e a notificação
  }, []);

  const releaseWake = useCallback(() => {
    const w = wake.current;
    wake.current = null;
    if (w) w.release().catch(() => undefined);
    setWakeLock((s) => (s === "unsupported" ? s : "off"));
  }, []);

  const requestWake = useCallback(async () => {
    const wl = (navigator as unknown as { wakeLock?: { request: (t: "screen") => Promise<WakeSentinel> } }).wakeLock;
    if (!wl) { setWakeLock("unsupported"); return; }
    try {
      if (wake.current) return;
      wake.current = await wl.request("screen");
      setWakeLock("on");
    } catch { setWakeLock("off"); }
  }, []);

  // GPS ligado só enquanto espera a primeira posição ou grava
  useEffect(() => {
    const live = status === "requesting_location" || status === "recording";
    if (!live) { stopWatch(); return; }
    if (watch.current !== null || nativeWatch.current !== null) return;
    const bg = backgroundGeo();
    if (bg) {
      // pela ponte do Capacitor o id pode vir direto (string) ou numa Promise: Promise.resolve cobre os dois
      nativeWatch.current = Promise.resolve(bg.addWatcher(
        {
          backgroundTitle: t("Gravando corrida"),
          backgroundMessage: t("O EAPace está usando o GPS para registrar o seu treino. Toque para voltar ao app."),
          requestPermissions: true,
          stale: false,
          distanceFilter: 0,
        },
        (loc, err) => {
          if (err) {
            send(err.code === "NOT_AUTHORIZED" ? { type: "denied", now: Date.now() } : { type: "signal", problem: "unavailable" });
            return;
          }
          if (!loc) return;
          send({
            type: "fix", now: Date.now(), id: rRef.current.clientId ?? newId(),
            fix: { lat: loc.latitude, lon: loc.longitude, accuracy: loc.accuracy, ts: loc.time ?? Date.now(), alt: loc.altitude },
          });
        },
      ));
      nativeWatch.current.catch(() => send({ type: "signal", problem: "unavailable" }));
      return;
    }
    watch.current = navigator.geolocation.watchPosition(
      (p) => send({
        type: "fix", now: Date.now(), id: rRef.current.clientId ?? newId(),
        fix: { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy, ts: p.timestamp, alt: p.coords.altitude },
      }),
      (e) => send(e.code === 1 ? { type: "denied", now: Date.now() } : { type: "signal", problem: e.code === 3 ? "timeout" : "unavailable" }),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    );
  }, [status, stopWatch, send]);

  // tela acesa só gravando e só no NAVEGADOR (no app o GPS segue com a tela bloqueada); reativa ao voltar para a página
  useEffect(() => {
    if (status !== "recording" || background) { releaseWake(); return; }
    void requestWake();
    const onVisible = () => { if (document.visibilityState === "visible") { wake.current = null; void requestWake(); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [status, background, requestWake, releaseWake]);

  // relógio da tela
  useEffect(() => {
    if (status === "idle" || status === "saved" || status === "error") return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [status]);

  // aviso do navegador ao recarregar/fechar com gravação em andamento (a cópia do aparelho protege, mas o tempo parado não conta)
  useEffect(() => {
    if (status !== "recording" && status !== "paused" && status !== "finalizing") return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [status]);

  // sair da página encerra GPS e tela acesa (a gravação segue guardada no aparelho)
  useEffect(() => () => { stopWatch(); releaseWake(); void flush(); }, [stopWatch, releaseWake, flush]);

  // ---------------------------------------------------------------- comandos
  const start = useCallback(async () => {
    dispatch({ type: "check" });
    rRef.current = reduce(newRecorder(), { type: "check" });
    const fail = (problem: Exclude<Recorder["problem"], null>) => { rRef.current = reduce(rRef.current, { type: "precheck_fail", problem }); dispatch({ type: "precheck_fail", problem }); };
    if (!store.current) return fail("storage");
    if (interrupted(sessionsRef.current).length > 0) return fail("interrupted");
    if (!canStart(sessionsRef.current)) return fail("full");
    const problem = precheck(await readEnv());
    if (problem) return fail(problem);
    void navigator.storage?.persist?.().catch(() => undefined);   // pede ao navegador que não apague os dados sozinho
    // Android 13+: sem permissão de notificação o GPS segue gravando, mas o aviso "Gravando corrida" não aparece
    try { await nativePlugin("LocalNotifications")?.requestPermissions(); } catch { /* opcional */ }
    rRef.current = reduce(rRef.current, { type: "precheck_ok" });
    dispatch({ type: "precheck_ok" });
    setPersistFull(false); setPersistError(false); setSaveError(false); setSentDirect(null);
    persister.current = null;
  }, []);

  /**
   * Confirma: o corpo vai para o aparelho ANTES do envio. Se o aparelho não conseguir guardar (cheio ou bloqueado), a
   * gravação NÃO é marcada como salva: tenta enviar direto da memória (o servidor é idempotente) e, se isso também
   * falhar, continua no resumo para o usuário tentar de novo. Devolve true se a gravação ficou segura.
   */
  const confirm = useCallback(async (sport: "run" | "trail_run" | "walk" = "run") => {
    const body = buildBody(rRef.current, sport);
    const p = persister.current;
    if (!body || !p || !store.current) return false;
    setSaveError(false);
    try {
      await p.flush(snapshot(), Date.now());
      await confirmSession(store.current, p.session, body, Date.now());
    } catch {
      setPersistError(true);
      const res = await post(orgId, body);
      const same = (res.status === 200 || res.status === 201) && !!res.data?.activity_id && res.data.points_kept === body.points.length;
      if (!same) { setSaveError(true); return false; }
      setSentDirect(res.data!.activity_id!);
      store.current.deleteSession(p.id).catch(() => undefined);
      persister.current = null;
      rRef.current = reduce(rRef.current, { type: "confirm" });
      dispatch({ type: "confirm" });
      return true;
    }
    persister.current = null;
    rRef.current = reduce(rRef.current, { type: "confirm" });
    dispatch({ type: "confirm" });
    await refresh();
    void syncNow(true);
    return true;
  }, [orgId, refresh, syncNow]);

  const discard = useCallback(async () => {
    const id = persister.current?.id ?? rRef.current.clientId;
    persister.current = null;
    rRef.current = reduce(rRef.current, { type: "discard" });
    dispatch({ type: "discard" });
    if (id && store.current) { await store.current.deleteSession(id); await refresh(); }
  }, [refresh]);

  /** Traz de volta uma gravação interrompida (volta pausada; o tempo parado não conta). */
  const recover = useCallback(async (id: string) => {
    const s = store.current;
    if (!s || rRef.current.status !== "idle") return;
    const sess = await s.getSession(id);
    const rec = recoverInterrupted(await s.getEvents(id));
    if (!sess || !rec) { await s.deleteSession(id); await refresh(); return; }
    const p = new Persister(s, sess);
    if (rec.synthetic) p.push(rec.synthetic, "now" in rec.synthetic ? rec.synthetic.now : 0);
    persister.current = p;
    rRef.current = rec.state;
    dispatch({ type: "load", state: rec.state });
    await p.flush({ distanceM: rec.state.tracker.distanceM, activeS: Math.round(activeMs(rec.state, Date.now()) / 1000) }, Date.now());
    await refresh();
  }, [refresh]);

  const removeSession = useCallback(async (id: string) => {
    if (!store.current) return;
    await store.current.deleteSession(id);
    await refresh();
  }, [refresh]);

  return {
    r: r as Recorder, now, wakeLock, background, start, storage, sessions, syncing, persistFull, persistError, saveError, sentDirect,
    pause: () => send({ type: "pause", now: Date.now() }),
    resume: () => send({ type: "resume", now: Date.now() }),
    finish: () => send({ type: "finish", now: Date.now() }),
    confirm, discard, recover, removeSession,
    retry: () => syncNow(true),
    reset: () => { rRef.current = reduce(rRef.current, { type: "reset" }); dispatch({ type: "reset" }); setSentDirect(null); setSaveError(false); },
  };
}

/** Contexto do aparelho (B5): decide se o comando de gravar existe. Lê capacidades; nunca só largura nem só user-agent. */
export function useCaptureContext() {
  const read = (): CaptureContext => {
    const mm = (q: string) => typeof matchMedia !== "undefined" && matchMedia(q).matches;
    const uad = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData;
    return captureContext({
      coarsePrimary: mm("(pointer: coarse)"), coarseAny: mm("(any-pointer: coarse)"), maxTouchPoints: navigator.maxTouchPoints ?? 0,
      uaMobile: typeof uad?.mobile === "boolean" ? uad.mobile : null, isApp,
    });
  };
  const [ctx, setCtx] = useState<CaptureContext>(read);
  useEffect(() => {
    if (typeof matchMedia === "undefined") return;
    const q = matchMedia("(pointer: coarse)");
    const on = () => setCtx(read());
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, []);
  return ctx;
}
